import { css } from 'styled-components';

/*
 * Design tokens, copied verbatim from the marketing site
 * (marketing-site-nextjs: src/styles/tailwind.css). Same names, same values,
 * so both sites stay in step. Light lives on :root, dark on .dark (the class
 * next-themes puts on <html>).
 *
 * See docs/design-system.md for when to use each token.
 */
export const tokens = css`
  :root {
    /* Brand primitives */
    --brand-indigo: #4b4df1;
    --brand-pink: #e4517f;
    --brand-yellow: #f4f389;
    --ink: #4c4c71;
    --ink-subtle: #8282a1;
    --status-danger: #eb3449;
    --status-info: #32c5ff;
    --status-success: #2dd8a4;
    --status-warning: #f2c94c;
    /* Text and icons on warning tints, where the yellow itself is illegible. */
    --warning-foreground: #8a5a00;
    --gradient-brand: linear-gradient(135deg, var(--brand-pink) 0%, var(--brand-indigo) 100%);
    --gradient-soft: linear-gradient(115deg, #fbe0e8 0%, #ece6f8 45%, #eceef3 100%);

    /* Semantic tokens: light */
    --radius: 0.75rem;
    --background: #ffffff;
    --foreground: var(--ink);
    --card: #ffffff;
    --card-foreground: var(--ink);
    --popover: #ffffff;
    --popover-foreground: var(--ink);
    --primary: var(--brand-indigo);
    --primary-foreground: #ffffff;
    --secondary: #f1f1fb;
    --secondary-foreground: var(--ink);
    --muted: #f4f4f8;
    --muted-foreground: #6b6b8c;
    --accent: #ecebfd;
    --accent-foreground: var(--ink);
    /* Darker than the brand danger swatch so small text passes AA on white. */
    --destructive: #d92435;
    --destructive-foreground: #ffffff;
    --border: #e4e4ef;
    --input: #e4e4ef;
    --ring: var(--brand-indigo);

    /*
     * Derived radii. The marketing site computes these from a 16px rem; this
     * site's root font size is fluid (16-18px), so they are pinned in px.
     */
    --radius-sm: 8px;
    --radius-md: 10px;
    --radius-lg: 12px;
    --radius-xl: 16px;
    --radius-2xl: 20px;
    --radius-3xl: 24px;
    --radius-4xl: 32px;

    /* Layout */
    --font-sans: var(--font-switzer), ui-sans-serif, system-ui, sans-serif;
    --nav-height: 64px;
    --container-max: 1280px;

    /* Elevation (marketing uses Tailwind's shadow-md / shadow-lg / shadow-xl) */
    --shadow-sm: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
    --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
  }

  .dark {
    --gradient-soft: linear-gradient(
      115deg,
      #2a1a24 0%,
      #231a2a 22%,
      #1c1a2e 45%,
      #171724 70%,
      #121212 100%
    );
    --warning-foreground: #f3c872;
    --background: #121212;
    --foreground: rgba(255, 255, 255, 0.87);
    --card: #232323;
    --card-foreground: rgba(255, 255, 255, 0.87);
    --popover: #232323;
    --popover-foreground: rgba(255, 255, 255, 0.87);
    --primary: #6c6ef5;
    --primary-foreground: #ffffff;
    --secondary: #2a2a2a;
    --secondary-foreground: #f9f9f9;
    --muted: #2a2a2a;
    --muted-foreground: #b2b3c4;
    --accent: #2e2e3a;
    --accent-foreground: #f9f9f9;
    --destructive: #ff5a6c;
    --destructive-foreground: #ffffff;
    --border: rgba(255, 255, 255, 0.1);
    --input: rgba(255, 255, 255, 0.15);
    --ring: #6c6ef5;
  }
`;

/*
 * Base element styles. Selectors are wrapped in :where() so they carry zero
 * specificity: any component rule wins without needing !important.
 */
export const baseStyles = css`
  html {
    /* In-page anchors clear the sticky navbar. */
    scroll-padding-top: 80px;
  }

  body {
    background: var(--background);
    color: var(--foreground);
    font-family: var(--font-sans);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  :where(a) {
    color: var(--primary);
  }

  :where(a, button, input, select, textarea, summary, [role='button'], [tabindex]):where(:focus-visible) {
    outline: 2px solid var(--ring);
    outline-offset: 2px;
  }

  ::selection {
    background: color-mix(in oklab, var(--primary) 25%, transparent);
  }

  /* Every interactive element shows a pointer; disabled controls do not. */
  :where(a[href], button, summary, select, label[for], [role='button'], [role='tab'], [role='menuitem'], [role='menuitemradio'], [role='option'], [role='link'], input[type='checkbox'], input[type='radio'], input[type='submit']) {
    cursor: pointer;
  }

  :where(button:disabled, [aria-disabled='true']) {
    cursor: not-allowed;
  }
`;
